import { Tabs } from "../../primitives/Tabs";
import { Button } from "../../primitives/Button";
import { Calendar } from "lucide-react";
import type { Promotion } from "../../../api/catalog";
import type { PriceBook } from "../../../api/catalog";
import type { Product } from "../../../api/catalog";
import { PromotionTabDetails } from "./PromotionTabDetails";
import { PromotionTabCiblage } from "./PromotionTabCiblage";
import { PromotionTabRegles } from "./PromotionTabRegles";
import { PromotionTabSimulation } from "./PromotionTabSimulation";
import { PromotionTabUsage } from "./PromotionTabUsage";

function getStatusLabel(promo: Promotion): { label: string; tone: string } {
  const now = new Date();
  const start = new Date(promo.start_date);
  const end = new Date(promo.end_date);
  if (promo.status === "archived") return { label: "Archivée", tone: "default" };
  if (promo.status === "draft") return { label: "Brouillon", tone: "default" };
  if (promo.status === "paused") return { label: "Pausée", tone: "warning" };
  if (end < now) return { label: "Expirée", tone: "danger" };
  if (start > now) return { label: "À venir", tone: "default" };
  return { label: "Active", tone: "success" };
}

type PromotionDetailPanelProps = {
  promotion: Promotion;
  priceBooks: PriceBook[];
  products: Product[];
  activeTab: string;
  onTabChange: (id: string) => void;
  onActivate: () => void;
  onPause: () => void;
  onArchive: () => void;
  onEdit: () => void;
  isLocked: boolean;
};

export function PromotionDetailPanel({
  promotion,
  priceBooks,
  products,
  activeTab,
  onTabChange,
  onActivate,
  onPause,
  onArchive,
  onEdit,
  isLocked,
}: PromotionDetailPanelProps) {
  const status = getStatusLabel(promotion);
  const valueLabel =
    promotion.promotion_type === "percentage"
      ? `-${promotion.value}%`
      : promotion.promotion_type === "fixed_amount"
      ? `-${promotion.value} CAD`
      : `${promotion.value} CAD (fixe)`;

  return (
    <div className="flex flex-1 min-w-0 h-full">
      {/* Left: summary + tabs */}
      <div className="w-56 shrink-0 border-r border-border flex flex-col bg-surface-muted/30">
        <div className="p-4 border-b border-border">
          <div className="rounded-lg border border-border bg-surface p-3 space-y-2">
            <div className="font-medium text-text truncate" title={promotion.name}>
              {promotion.name}
            </div>
            <div
              className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                status.tone === "success"
                  ? "bg-success/10 text-success"
                  : status.tone === "warning"
                  ? "bg-warning/10 text-warning"
                  : status.tone === "danger"
                  ? "bg-danger/10 text-danger"
                  : "bg-muted/10 text-muted"
              }`}
            >
              {status.label}
            </div>
            <div className="text-sm text-muted">{valueLabel}</div>
            <div className="text-xs text-muted flex items-center gap-1">
              <Calendar size={12} />
              {new Date(promotion.start_date).toLocaleDateString("fr-FR")} →{" "}
              {new Date(promotion.end_date).toLocaleDateString("fr-FR")}
            </div>
            <div className="text-xs text-muted">
              {promotion.code ? `Code: ${promotion.code}` : "Auto"}
            </div>
          </div>
        </div>
        <Tabs
          tabs={[
            { id: "details", label: "Détails" },
            { id: "ciblage", label: "Ciblage" },
            { id: "regles", label: "Règles" },
            { id: "simulation", label: "Aperçu / Simulation" },
            { id: "usage", label: "Utilisation / Audit" },
          ]}
          activeId={activeTab}
          onChange={onTabChange}
          variant="underline"
        />
      </div>

      {/* Right: content */}
      <div className="flex-1 min-w-0 overflow-y-auto p-4">
        {activeTab === "details" && (
          <PromotionTabDetails
            promotion={promotion}
            onEdit={onEdit}
            onActivate={onActivate}
            onPause={onPause}
            onArchive={onArchive}
            isLocked={isLocked}
          />
        )}
        {activeTab === "ciblage" && (
          <PromotionTabCiblage
            promotion={promotion}
            priceBooks={priceBooks}
            products={products}
            isLocked={isLocked}
          />
        )}
        {activeTab === "regles" && (
          <PromotionTabRegles promotion={promotion} isLocked={isLocked} />
        )}
        {activeTab === "simulation" && (
          <PromotionTabSimulation promotion={promotion} priceBooks={priceBooks} products={products} />
        )}
        {activeTab === "usage" && <PromotionTabUsage promotionId={promotion.id} />}
      </div>
    </div>
  );
}
